import type { ResultApi } from '@netpurpose/api'
import type { QueryClient } from '@tanstack/react-query'
import type { Dispatch } from 'react'
import { AlertBannerType } from '#components/common/AlertBanner'
import {
  type Action,
  type Actions,
  type DataDictionaryExport,
  type EntityExport,
  type PortfolioCreation,
  type PortfolioExport,
  type State,
  type Task,
  TaskType,
} from '#context/tasks'
import { assertNever, entries } from '#services/typeHelpers'
import type { PortfolioPAIsExport, PortfolioSDGRevenueExport, PortfolioSDGsExport } from './types'

const getTaskResultAndDispatchStatusUpdate = async <T extends Task>(
  resultApi: ResultApi,
  token: string,
  taskType: T['type'],
  dispatch: Dispatch<Action<T>>,
  showAlertBanner: (type: unknown, text?: string | undefined) => void,
  queryClient: QueryClient,
) => {
  const result = await resultApi.getResult<T['result']>(token)
  // @ts-expect-error - Type 'T["result"] | undefined' is not assignable to type 'T["result"]'
  dispatch({
    actionType: 'statusUpdate',
    taskType,
    token,
    newStatus: result.status,
    result: result.data,
  })

  queryClient.invalidateQueries({ queryKey: ['listExports'] })

  if (result.status === 'failed') {
    showAlertBanner(AlertBannerType.GeneralError)
  }
}

export const checkPendingTaskResults = (
  resultApi: ResultApi,
  state: State,
  dispatch: Dispatch<Actions>,
  queryClient: QueryClient,
  showAlertBanner: (type: unknown, text?: string | undefined) => void,
) => {
  entries(state).forEach(([taskType, taskStateById]) => {
    entries(taskStateById)
      .filter(([_, taskState]) => taskState.status === 'pending')
      .forEach(async ([token]) => {
        switch (taskType) {
          case TaskType.PortfolioCreation: {
            const result = await resultApi.getResult<PortfolioCreation['result']>(token)
            if (result.status === 'unknown') {
              return null
            }
            if (result.status !== 'pending') {
              queryClient.invalidateQueries({ queryKey: ['portfolios'] })
              queryClient.invalidateQueries({ queryKey: ['portfolioSnapshots'] })
              queryClient.invalidateQueries({ queryKey: ['listExports'] })
            }
            if (result.status === 'failed') {
              showAlertBanner(AlertBannerType.GeneralError)
            }
            // @ts-expect-error - "result" type 'string | undefined' is not assignable to type 'string'.
            return dispatch({
              actionType: 'statusUpdate',
              taskType,
              token,
              newStatus: result.status,
              result: result.data,
            })
          }
          case TaskType.PortfolioExport: {
            return getTaskResultAndDispatchStatusUpdate<PortfolioExport>(
              resultApi,
              token,
              taskType,
              dispatch,
              showAlertBanner,
              queryClient,
            )
          }
          case TaskType.PortfolioPAIsExport: {
            return getTaskResultAndDispatchStatusUpdate<PortfolioPAIsExport>(
              resultApi,
              token,
              taskType,
              dispatch,
              showAlertBanner,
              queryClient,
            )
          }
          case TaskType.PortfolioSDGsExport: {
            return getTaskResultAndDispatchStatusUpdate<PortfolioSDGsExport>(
              resultApi,
              token,
              taskType,
              dispatch,
              showAlertBanner,
              queryClient,
            )
          }
          case TaskType.PortfolioSDGRevenueExport: {
            return getTaskResultAndDispatchStatusUpdate<PortfolioSDGRevenueExport>(
              resultApi,
              token,
              taskType,
              dispatch,
              showAlertBanner,
              queryClient,
            )
          }
          case TaskType.EntityExport: {
            return getTaskResultAndDispatchStatusUpdate<EntityExport>(
              resultApi,
              token,
              taskType,
              dispatch,
              showAlertBanner,
              queryClient,
            )
          }
          case TaskType.DataDictionaryExport: {
            return getTaskResultAndDispatchStatusUpdate<DataDictionaryExport>(
              resultApi,
              token,
              taskType,
              dispatch,
              showAlertBanner,
              queryClient,
            )
          }
          default: {
            return assertNever(taskType)
          }
        }
      })
  })
}
